/**
 * CompanyKycDocsPage — owner uploads / replaces KYC documents.
 *
 * Required docs (server-enforced for submit-for-review):
 *   local company    → commercial_register, tax_certificate,
 *                      owner_id_front, owner_id_back
 *   international    → + import_license
 *
 * Optional: bank_certificate, other
 *
 *   Server: GET /api/cargo/me/company        (returns kyc_docs[])
 *           POST /api/cargo/me/company/:id/kyc-docs
 *
 * After admin approval → review_status='accepted'. After rejection →
 * review_status='rejected' + review_note. Owner can re-upload → server
 * archives the rejected row via replaced_by_id and creates a fresh one.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, ShieldCheck, Loader, FileText, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { useMyCompany, useUploadKycDoc } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { FileUploadField } from '@/components/common/FileUploadField';
import type { CompanyKycDoc, KycDocKind } from '@/types/company';

const REQUIRED_LOCAL: KycDocKind[] = [
  'commercial_register', 'tax_certificate',
  'owner_id_front', 'owner_id_back',
];
const REQUIRED_INTL: KycDocKind[] = [
  'commercial_register', 'tax_certificate', 'import_license',
  'owner_id_front', 'owner_id_back',
];
const OPTIONAL_KINDS: KycDocKind[] = ['bank_certificate', 'other'];

const KIND_LABEL: Record<KycDocKind, string> = {
  commercial_register: 'السجل التجاري',
  tax_certificate:     'الشهادة الضريبية',
  import_license:      'رخصة الاستيراد',
  owner_id_front:      'هوية المالك (وجه)',
  owner_id_back:       'هوية المالك (خلف)',
  bank_certificate:    'شهادة بنكية',
  other:               'وثيقة أخرى',
};

const REVIEW_STATUS_DISPLAY: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  submitted: { label: 'بانتظار المراجعة', color: 'text-info',    icon: Loader },
  accepted:  { label: 'مقبولة',           color: 'text-success', icon: CheckCircle2 },
  rejected:  { label: 'مرفوضة',           color: 'text-error',   icon: AlertCircle },
};

export default function CompanyKycDocsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading } = useMyCompany();

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  const required = company.company_type === 'international' ? REQUIRED_INTL : REQUIRED_LOCAL;
  const docs = (company as any).kyc_docs as CompanyKycDoc[] | undefined ?? [];

  // Live docs by kind — only consider non-replaced rows.
  const docsByKind = new Map<KycDocKind, CompanyKycDoc>();
  for (const d of docs) {
    if (d.replaced_by_id != null) continue;
    docsByKind.set(d.kind, d);
  }

  const requiredMissing = required.filter((k) => {
    const d = docsByKind.get(k);
    return !d || d.review_status === 'rejected';
  });

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <ShieldCheck size={20} /> {t('kyc_docs_title', 'وثائق KYC')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('kyc_docs_subtitle', 'الوثائق الإلزامية لاعتماد شركتك. الإدارة تراجع كل وثيقة بعد الرفع.')}
        </p>

        {/* Summary */}
        {requiredMissing.length > 0 ? (
          <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 mb-4 text-sm text-text-primary flex items-start gap-2">
            <AlertCircle size={16} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <strong>
                {t('kyc_missing_count', '{n} وثيقة إلزامية ناقصة').replace('{n}', String(requiredMissing.length))}
              </strong>
              <p className="text-xs text-text-secondary mt-1">
                {t('kyc_missing_hint', 'ارفعها كلها قبل تقديم الشركة للمراجعة.')}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-success/10 border border-success/30 rounded-xl p-3 mb-4 text-sm text-text-primary flex items-start gap-2">
            <CheckCircle2 size={16} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <strong>{t('kyc_all_uploaded', 'كل الوثائق الإلزامية مرفوعة')}</strong>
            </div>
          </div>
        )}

        {/* Required docs */}
        <h2 className="text-sm font-bold text-text-secondary mt-5 mb-2">
          {t('kyc_required', 'الوثائق الإلزامية')} ({required.length})
        </h2>
        <div className="space-y-3">
          {required.map((kind) => (
            <KycRow
              key={kind}
              kind={kind}
              doc={docsByKind.get(kind)}
              companyId={company.id}
            />
          ))}
        </div>

        {/* Optional docs */}
        <h2 className="text-sm font-bold text-text-secondary mt-6 mb-2">
          {t('kyc_optional', 'وثائق إضافية (اختيارية)')}
        </h2>
        <div className="space-y-3">
          {OPTIONAL_KINDS.map((kind) => (
            <KycRow
              key={kind}
              kind={kind}
              doc={docsByKind.get(kind)}
              companyId={company.id}
              optional
            />
          ))}
        </div>

        <p className="text-xs text-text-tertiary mt-6">
          {t('kyc_replace_hint', '💡 لو رُفضت وثيقة، ارفع نسخة جديدة وستُرسَل تلقائياً للمراجعة. النسخة القديمة تبقى للأرشيف.')}
        </p>
      </div>
    </AppLayout>
  );
}

function KycRow({
  kind, doc, companyId, optional,
}: {
  kind: KycDocKind;
  doc?: CompanyKycDoc;
  companyId: number;
  optional?: boolean;
}) {
  const { t } = useTranslation();
  const upload = useUploadKycDoc(companyId);
  const status = doc?.review_status as keyof typeof REVIEW_STATUS_DISPLAY | undefined;
  const statusDisplay = status ? REVIEW_STATUS_DISPLAY[status] : null;
  const isRejected = status === 'rejected';
  const isAccepted = status === 'accepted';

  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={14} className="text-text-tertiary" />
            <span className="font-bold text-text-primary">{KIND_LABEL[kind]}</span>
            {!optional && (
              <span className="text-[10px] px-1.5 py-0.5 bg-warning/10 text-warning rounded font-bold">
                {t('required', 'إلزامية')}
              </span>
            )}
            {optional && (
              <span className="text-[10px] px-1.5 py-0.5 bg-border/40 text-text-secondary rounded">
                {t('optional', 'اختياري')}
              </span>
            )}
          </div>
          {statusDisplay && (
            <div className={`flex items-center gap-1 text-xs ${statusDisplay.color}`}>
              <statusDisplay.icon size={12} />
              <span>{statusDisplay.label}</span>
              {doc?.uploaded_at && (
                <span className="text-text-tertiary ml-2">
                  · {new Date(doc.uploaded_at).toLocaleDateString('ar-SY')}
                </span>
              )}
            </div>
          )}
          {isRejected && doc?.rejection_reason && (
            <div className="mt-2 p-2 bg-error/5 border border-error/30 rounded text-xs text-error">
              <strong>سبب الرفض:</strong> {doc.rejection_reason}
            </div>
          )}
        </div>
      </div>

      {/* Upload widget */}
      {!isAccepted && (
        <FileUploadField
          kind="document"
          label={
            doc
              ? t('replace_file', 'استبدال الملف')
              : t('upload_file', 'رفع الملف')
          }
          onUploaded={(url) => {
            upload.mutate({ kind, fileUrl: url });
          }}
          disabled={upload.isPending}
        />
      )}

      {isAccepted && doc?.file_url && (
        <a
          href={doc.file_url}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
        >
          <FileText size={12} /> {t('view_file', 'عرض الملف')}
        </a>
      )}
    </div>
  );
}
